<template>
  <div class="stl-root">

    <b-modal title="Here are details on the statuses and filters used for the VSM" size="lg" v-model="view_more_info" ok-only>
      <br/>
      Use this page to view and manage support tickets.
      <br/><br/>
      <b>Create A Support Ticket</b>
      <br/><br/>
      Click 'Create Ticket' to get started with a new ticket
      <br/><br/>
      <b>Search For A Support Ticket</b>
      <br/><br/>
      Use the search tools to find existing tickets
      <br/><br/>
      <b>Update A Support Ticket</b>
      <br/><br/>
      Click the Edit Icon to update a ticket
    </b-modal>

    <!-- filter row 1: date range, dropdowns, actions -->
    <div style="font-size: 14px; font-weight: bold; margin: 10px 0; gap: 6px;" class="d-flex align-items-center justify-content-center flex-wrap">
      <span style="white-space: nowrap; display: inline-flex; align-items: center; gap: 4px;">
        <button type="button" class="date-nav-btn" @click="shift_detail_graph_date(-1)">&lt;</button>
        <date-picker style="font-size: 14px; font-weight: bold; width:120px; display: inline-block;" v-model="details_fromDate" :config="options" :placeholder="details_fromDate"/>
        <span style="margin: 0 2px;">to</span>
        <date-picker style="font-size: 14px; font-weight: bold; width:120px; display: inline-block;" v-model="details_toDate" :config="options" :placeholder="details_toDate"/>
        <button type="button" class="date-nav-btn" @click="shift_detail_graph_date(1)">&gt;</button>
      </span>
      <span style="white-space: nowrap">&nbsp;&nbsp;Type:&nbsp;<b-form-select v-model="selected_priority" :options="priority_options" style="font-size: 14px; font-weight: bold; width:140px"/></span>
      <span style="white-space: nowrap">&nbsp;&nbsp;Category:&nbsp;<b-form-select v-model="selected_category" :options="category_options" style="font-size: 14px; font-weight: bold; width:150px"/></span>
      <span style="white-space: nowrap">&nbsp;&nbsp;Status:&nbsp;<b-form-select v-model="selected_status" :options="status_options" style="font-size: 14px; font-weight: bold; width:140px"/></span>
      <span style="white-space: nowrap">
        &nbsp;&nbsp;<b-button v-on:click.prevent="load_data">Search</b-button>
        &nbsp;&nbsp;<b-button variant="success" size="md" :to="{ name: 'support_ticket_create' }"><span style="color:white">Create Ticket</span></b-button>
      </span>
    </div>
    <!-- filter row 2: admin controls. flex gap replaces inline &nbsp; spacing
         so wrap-lines stay evenly spaced; pipes are dedicated separator spans
         so they don't orphan at the start of a wrapped line. gated on
         effectiveRole (not user.role) so the Muggle preview toggle hides
         the toolbar along with the rest of the admin chrome. -->
    <div v-if="effectiveRole === 'admin'" class="stl-filter-row">
      <span class="stl-fbar-count">Results: {{items.length}} of {{items_full.length}}</span>
      <span class="stl-fbar-sep"></span>
      <span class="stl-fbar-field">Customer: <b-form-select v-model="selected_tenant" class="stl-fbar-select" style="width:200px" :options="search_tenant_options"/></span>
      <span class="stl-fbar-field">Assigned: <b-form-select v-model="selected_assigned" :options="assigned_options" class="stl-fbar-select" style="width:120px"/></span>
      <span class="stl-fbar-field">Urgency: <b-form-select v-model="selected_urgency" class="stl-fbar-select" style="width:100px" :options="urgency_options"/></span>
      <b-form-input class="stl-fbar-input" v-model="search_text" placeholder="subject text"/>
      <b-form-checkbox v-model="vip_only" class="stl-fbar-vip" title="Only show tickets for VIP tenants">VIP</b-form-checkbox>
      <span class="stl-fbar-sep"></span>
      <span class="stl-btn-group stl-dev-team-chips">
        <button
          type="button"
          class="stl-dev-chip"
          :class="{ 'stl-dev-chip--selected': isUnassignedChipSelected() }"
          title="Unassigned — open tickets"
          aria-label="Unassigned — open tickets"
          v-on:click.prevent="show_unassigned"
        >
          <span class="stl-dev-chip-icon stl-dev-chip-icon--unassigned" aria-hidden="true">
            <i class="fa fa-user"></i>
          </span>
        </button>
        <button
          v-for="member in dev_team_filters"
          :key="member.user"
          type="button"
          class="stl-dev-chip"
          :class="{ 'stl-dev-chip--selected': isDevTeamChipSelected(member) }"
          :title="`${member.name} — active tickets`"
          v-on:click.prevent="show_active(member.user)"
        >
          <img :src="member.image" :alt="member.name" class="stl-dev-chip-img" />
        </button>
      </span>
      <span class="stl-fbar-sep"></span>
      <span class="stl-btn-group">
        <b-button size="sm" v-on:click.prevent="show_all_open">All</b-button>
        <b-button size="sm" v-on:click.prevent="show_all_current_account">Cust</b-button>
      </span>
    </div>

    <!-- mobile list-view: list-to-detail navigation matching the help
         articles pattern (≤768px cut-off). the card acts as the primary
         tap target → /support_ticket_details?tenant_id=…&id=…. on
         tablet/desktop (>768px) the full b-table below takes over. -->
    <div v-if="isMobileView" class="stl-mobile-list">
      <!-- skeleton cards while load_data runs. shape mirrors the real
           card (id chip + priority tag pill, multi-line subject, meta
           row, admin assignee) so the page settles into final content
           with minimal layout shift. -->
      <template v-if="loading">
        <div v-for="n in 4" :key="`stl-sk-${n}`" class="stl-mobile-card stl-mobile-card--skeleton">
          <div class="stl-mobile-card-head">
            <span class="stl-skeleton stl-skeleton--id"></span>
            <span class="stl-skeleton stl-skeleton--tag"></span>
          </div>
          <span class="stl-skeleton stl-skeleton--subject"></span>
          <span v-if="n % 2 === 0" class="stl-skeleton stl-skeleton--subject stl-skeleton--subject-2"></span>
          <div class="stl-skeleton-meta-row">
            <span class="stl-skeleton stl-skeleton--meta-status"></span>
            <span class="stl-skeleton stl-skeleton--meta-tenant"></span>
            <span class="stl-skeleton stl-skeleton--meta-time"></span>
          </div>
          <span v-if="effectiveRole === 'admin'" class="stl-skeleton stl-skeleton--admin"></span>
        </div>
      </template>
      <router-link
        v-for="item in paginatedItems"
        v-if="!loading"
        :key="`mbl-${item.tenant_id}-${item.id}`"
        :to="{ name: 'support_ticket_details', query: { tenant_id: item.tenant_id, id: item.id } }"
        class="stl-mobile-card"
        :class="{
          'stl-mobile-card--muted': ['completed','cancelled'].includes(item.ticket_status),
          'stl-mobile-card--vip': !!getTenantVip(item.tenant_id),
        }">
        <div class="stl-mobile-card-head">
          <span class="stl-mobile-card-id">#{{ item.id }}</span>
          <span v-if="getPriorityTag(item.priority)"
                class="priority-tag priority-tag--labeled"
                :class="[
                  getPriorityTag(item.priority).variant,
                  { 'priority-tag-muted': ['completed','cancelled'].includes(item.ticket_status) },
                  { 'priority-tag--strike': item.priority === 'urgent' && !['completed','cancelled'].includes(item.ticket_status) },
                ]">
            <i class="fa" :class="getPriorityTag(item.priority).icon" aria-hidden="true"></i>
            <span class="priority-tag-label">{{ getPriorityTag(item.priority).compactLabel }}</span>
          </span>
        </div>
        <div class="stl-mobile-card-subject">{{ item.subject || '(no subject)' }}</div>
        <div class="stl-mobile-card-meta">
          <span class="stl-mobile-card-status">{{ statusShortLabel(item.ticket_status) }}</span>
          <span class="stl-mobile-card-sep">·</span>
          <span class="stl-mobile-card-tenant" :class="{ 'is-vip': !!getTenantVip(item.tenant_id) }">
            {{ getOptionText(user.tenant_options, item.tenant_id) }}
          </span>
          <span class="stl-mobile-card-sep">·</span>
          <span class="stl-mobile-card-updated">{{ formatDateTimeCompact(item.updated_at) }}</span>
        </div>
        <div v-if="effectiveRole === 'admin'" class="stl-mobile-card-admin">
          <span class="stl-mobile-card-assigned">{{ getOptionText(assigned_options, item.assigned_to) || 'Unassigned' }}</span>
        </div>
      </router-link>
      <div v-if="!loading && items.length === 0" class="stl-mobile-empty">No tickets match your filters.</div>
    </div>

    <div v-show="!isMobileView" class="stl-table-wrap">
      <div class="stl-table-inner">
      <b-table :busy="loading"
               :items="sortedItems"
               :fields="filteredFields"
               :current-page="currentPage"
               :per-page="perPage"
               :sort-by.sync="sortBy"
               :sort-desc.sync="sortDesc"
               no-local-sorting
               :tbody-tr-class="rowClass"
               responsive>

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <template slot="id" slot-scope="row">
          <router-link :to="{ name: 'support_ticket_details', query: { tenant_id: row.item.tenant_id, id: row.item.id } }">
            {{ row.item.id }}
          </router-link>
        </template>

        <template slot="tenant_id" slot-scope="row">
          <span
            class="tenant-name"
            :class="{
              'is-vip': !!getTenantVip(row.item.tenant_id),
              'is-muted': ['completed', 'cancelled'].includes(row.item.ticket_status)
            }"
            :title="getTenantVip(row.item.tenant_id) ? ('VIP — ' + getTenantVip(row.item.tenant_id).vip_reason) : ''"
          >{{ getOptionText(user.tenant_options, row.item.tenant_id) }}</span>
        </template>

        <template slot="created_at" slot-scope="row">
          <span class="stl-dt" :title="row.item.created_at">{{ formatDateTimeCompact(row.item.created_at) }}</span>
        </template>

        <template slot="updated_at" slot-scope="row">
          <span class="stl-dt" :class="{ 'stl-dt-overdue': row.item.updated_at_overdue }" :title="row.item.updated_at">
            {{ formatDateTimeCompact(row.item.updated_at) }}
          </span>
        </template>

        <template slot="estimated_finish_date" slot-scope="row">
          <span class="stl-dt" :title="row.item.estimated_finish_date || ''">
            {{ formatTargetDateCompact(row.item.estimated_finish_date) }}
          </span>
        </template>

        <template slot="assigned_to" slot-scope="row">
          <template v-if="effectiveRole === 'admin'">
            <b-form-select
              v-if="isInlineEditing(row.item, 'assigned_to')"
              :value="row.item.assigned_to"
              :options="inlineAssignedOptions"
              :disabled="isInlineSaving(row.item, 'assigned_to')"
              size="sm"
              class="stl-inline-select"
              @change="updateTicketInline(row.item, 'assigned_to', $event)"
              @blur="onInlineEditBlur(row.item, 'assigned_to')"
            />
            <span
              v-else
              class="stl-inline-readonly stl-person-display"
              tabindex="0"
              role="button"
              :title="displayUserName(row.item.assigned_to) || 'Unassigned'"
              @click="startInlineEdit(row.item, 'assigned_to', $event)"
              @keydown.enter.space.prevent="startInlineEdit(row.item, 'assigned_to', $event)"
            >
              <img
                v-if="initekTeamImage(row.item.assigned_to)"
                :src="initekTeamImage(row.item.assigned_to)"
                :alt="displayUserName(row.item.assigned_to)"
                class="stl-team-avatar"
              />
              <span v-else>{{ getOptionText(inlineAssignedOptions, row.item.assigned_to) || 'Unassigned' }}</span>
            </span>
          </template>
          <div
            v-else
            class="stl-person-display"
            :title="initekTeamImage(row.item.assigned_to) ? displayUserName(row.item.assigned_to) : null"
          >
            <img
              v-if="initekTeamImage(row.item.assigned_to)"
              :src="initekTeamImage(row.item.assigned_to)"
              :alt="displayUserName(row.item.assigned_to)"
              class="stl-team-avatar"
            />
            <span v-else>{{ getOptionText(inlineAssignedOptions, row.item.assigned_to) || 'Unassigned' }}</span>
          </div>
        </template>

        <template slot="username" slot-scope="row">
          {{ row.item.created_by_name || row.item.username.split('@')[0] }}
        </template>

        <template slot="updated_by" slot-scope="row">
          <div
            v-if="row.item.updated_by"
            class="stl-person-display"
            :title="initekTeamImage(row.item.updated_by)
              ? displayUserName(row.item.updated_by, row.item.updated_by_name)
              : null"
          >
            <img
              v-if="initekTeamImage(row.item.updated_by)"
              :src="initekTeamImage(row.item.updated_by)"
              :alt="displayUserName(row.item.updated_by, row.item.updated_by_name)"
              class="stl-team-avatar"
            />
            <span v-else>{{ displayUserName(row.item.updated_by, row.item.updated_by_name) }}</span>
          </div>
        </template>

        <template slot="subject" slot-scope="row">
          <template v-if="effectiveRole === 'admin'">
            <b-form-input
              v-if="isInlineEditing(row.item, 'subject')"
              v-model="inlineDraft"
              size="sm"
              class="stl-inline-input stl-subject"
              :disabled="isInlineSaving(row.item, 'subject')"
              @keydown.enter.prevent="commitInlineSubject(row.item)"
              @keydown.esc.prevent="clearInlineEdit"
              @blur="onInlineSubjectBlur(row.item)"
            />
            <span
              v-else
              class="stl-subject stl-inline-readonly"
              tabindex="0"
              role="button"
              :title="row.item.subject || ''"
              @click="startInlineEdit(row.item, 'subject', $event)"
              @keydown.enter.space.prevent="startInlineEdit(row.item, 'subject', $event)"
            >{{ row.item.subject }}</span>
          </template>
          <span v-else class="stl-subject" :title="row.item.subject || ''">{{ row.item.subject }}</span>
        </template>
        <template slot="ticket_status" slot-scope="row">
          <template v-if="effectiveRole === 'admin'">
            <b-form-select
              v-if="isInlineEditing(row.item, 'ticket_status')"
              :value="row.item.ticket_status"
              :options="inlineStatusOptions"
              :disabled="isInlineSaving(row.item, 'ticket_status')"
              size="sm"
              class="stl-inline-select stl-inline-select--status"
              @change="updateTicketInline(row.item, 'ticket_status', $event)"
              @blur="onInlineEditBlur(row.item, 'ticket_status')"
            />
            <span
              v-else
              class="stl-inline-readonly stl-status"
              tabindex="0"
              role="button"
              :title="getOptionText(status_options, row.item.ticket_status)"
              @click="startInlineEdit(row.item, 'ticket_status', $event)"
              @keydown.enter.space.prevent="startInlineEdit(row.item, 'ticket_status', $event)"
            >{{ statusShortLabel(row.item.ticket_status) }}</span>
          </template>
          <span v-else class="stl-status" :title="getOptionText(status_options, row.item.ticket_status)">
            {{ statusShortLabel(row.item.ticket_status) }}
          </span>
        </template>
        <template slot="HEAD_priority">
          Type
        </template>
        <template slot="priority" slot-scope="row">
          <template v-if="effectiveRole === 'admin'">
            <b-form-select
              v-if="isInlineEditing(row.item, 'priority')"
              :value="row.item.priority"
              :options="inlinePriorityOptions"
              :disabled="isInlineSaving(row.item, 'priority')"
              size="sm"
              class="stl-inline-select stl-inline-select--type"
              @change="updateTicketInline(row.item, 'priority', $event)"
              @blur="onInlineEditBlur(row.item, 'priority')"
            />
            <span
              v-else-if="getPriorityTag(row.item.priority)"
              class="priority-tag priority-tag--labeled stl-inline-readonly"
              :class="[
                getPriorityTag(row.item.priority).variant,
                {
                  'priority-tag-muted': ['completed', 'cancelled'].includes(row.item.ticket_status),
                  'priority-tag--strike': row.item.priority === 'urgent' && !['completed', 'cancelled'].includes(row.item.ticket_status),
                }
              ]"
              tabindex="0"
              role="button"
              :title="getPriorityTag(row.item.priority).label"
              :aria-label="getPriorityTag(row.item.priority).label"
              @click="startInlineEdit(row.item, 'priority', $event)"
              @keydown.enter.space.prevent="startInlineEdit(row.item, 'priority', $event)"
            >
              <i class="fa" :class="getPriorityTag(row.item.priority).icon" aria-hidden="true"></i>
              <span class="priority-tag-label">{{ getPriorityTag(row.item.priority).compactLabel }}</span>
            </span>
            <span
              v-else
              class="stl-inline-readonly"
              tabindex="0"
              role="button"
              :title="getOptionText(inlinePriorityOptions, row.item.priority)"
              @click="startInlineEdit(row.item, 'priority', $event)"
              @keydown.enter.space.prevent="startInlineEdit(row.item, 'priority', $event)"
            >{{ getOptionText(inlinePriorityOptions, row.item.priority) }}</span>
          </template>
          <template v-else>
            <span v-if="getPriorityTag(row.item.priority)"
                  class="priority-tag"
                  :class="[
                    getPriorityTag(row.item.priority).variant,
                    'priority-tag--labeled',
                    {
                      'priority-tag-muted': ['completed', 'cancelled'].includes(row.item.ticket_status),
                      'priority-tag--strike': row.item.priority === 'urgent' && !['completed', 'cancelled'].includes(row.item.ticket_status),
                    }
                  ]"
                  :title="getPriorityTag(row.item.priority).label"
                  :aria-label="getPriorityTag(row.item.priority).label">
              <i class="fa" :class="getPriorityTag(row.item.priority).icon" aria-hidden="true"></i>
              <span class="priority-tag-label">{{ getPriorityTag(row.item.priority).compactLabel }}</span>
            </span>
            <span v-else>{{ getOptionText(priority_options, row.item.priority) }}</span>
          </template>
        </template>
        <template slot="category" slot-scope="row">
          {{getOptionText(category_options, row.item.category)}}
        </template>
        <template slot="urgency" slot-scope="row">
          <template v-if="effectiveRole === 'admin'">
            <b-form-select
              v-if="isInlineEditing(row.item, 'urgency')"
              :value="row.item.urgency || null"
              :options="inlineUrgencyOptions"
              :disabled="isInlineSaving(row.item, 'urgency')"
              size="sm"
              class="stl-inline-select"
              @change="updateTicketInline(row.item, 'urgency', $event)"
              @blur="onInlineEditBlur(row.item, 'urgency')"
            />
            <span
              v-else-if="getUrgencyTag(row.item.urgency)"
              class="priority-tag priority-tag--labeled stl-inline-readonly"
              :class="getUrgencyTag(row.item.urgency).variant"
              tabindex="0"
              role="button"
              :title="getUrgencyTag(row.item.urgency).label"
              :aria-label="getUrgencyTag(row.item.urgency).label"
              @click="startInlineEdit(row.item, 'urgency', $event)"
              @keydown.enter.space.prevent="startInlineEdit(row.item, 'urgency', $event)"
            >
              <i class="fa" :class="getUrgencyTag(row.item.urgency).icon" aria-hidden="true"></i>
              <span class="priority-tag-label">{{ getUrgencyTag(row.item.urgency).compactLabel }}</span>
            </span>
            <span
              v-else
              class="stl-inline-readonly"
              tabindex="0"
              role="button"
              @click="startInlineEdit(row.item, 'urgency', $event)"
              @keydown.enter.space.prevent="startInlineEdit(row.item, 'urgency', $event)"
            >—</span>
          </template>
          <template v-else>
            <span
              v-if="getUrgencyTag(row.item.urgency)"
              class="priority-tag priority-tag--labeled"
              :class="getUrgencyTag(row.item.urgency).variant"
              :title="getUrgencyTag(row.item.urgency).label"
              :aria-label="getUrgencyTag(row.item.urgency).label"
            >
              <i class="fa" :class="getUrgencyTag(row.item.urgency).icon" aria-hidden="true"></i>
              <span class="priority-tag-label">{{ getUrgencyTag(row.item.urgency).compactLabel }}</span>
            </span>
            <span v-else>—</span>
          </template>
        </template>

        <template slot="actions" slot-scope="row">
          <!-- flex row keeps edit + delete on the same line; without it the
               <router-link> can collapse to block layout and push delete
               to the next row in narrow viewports. -->
          <div style="display: flex; align-items: center; gap: 4px;">
            <router-link :to="{ name: 'support_ticket_details', query: { tenant_id: row.item.tenant_id, id: row.item.id } }" style="display: inline-flex;">
              <img src="/edit.png" alt="Edit" style="height: 1rem; cursor: pointer"/>
            </router-link>
            <button
              v-if="user && user.role === 'admin'"
              type="button"
              class="stl-poke-btn"
              :disabled="isPokeSaving(row.item)"
              title="Send update email to customer"
              aria-label="Send update email to customer"
              @click="pokeTicket(row.item)"
            >
              <i class="fa" :class="isPokeSaving(row.item) ? 'fa-spinner fa-spin' : 'fa-hand-o-up'" aria-hidden="true"></i>
            </button>
            <img src="/delete.png" alt="Delete" style="height: 1.25rem; cursor: pointer" @click="delete_support_ticket(row.item.tenant_id,row.item.id)"/>
          </div>
        </template>
      </b-table>
      </div>
    </div>

    <!-- pagination lives outside the desktop table wrapper so the mobile
         card list uses the same control (both views bind to
         currentPage + slice off items). -->
    <b-row>
      <b-col>
        <b-pagination
            v-model="currentPage"
            :total-rows="items.length"
            :per-page="perPage"
            align="center"
        >
        </b-pagination>
      </b-col>
    </b-row>

  </div>
</template>

<script>
  import { delete_support_ticket, get_support_ticket, poke_support_ticket, search_support_tickets, update_support_ticket } from '../networking/apis'
  import { mapState } from "vuex";
  import { justDate, formatDateTimeCompact } from "@/helpers/dates";
  import { INITEK_TEAM_FILTER_CHIPS, initekTeamImageForUsername } from "@/helpers/initek_team_images";
  import moment from "moment/moment";

  const URGENCY_RANK = { low: 1, medium: 2, high: 3, emergency: 4 }
  const PRIORITY_RANK = { urgent: 5, minor: 4, question: 3, enhancement: 2, feature: 1 }

  export default {
    name: 'support_ticket_list',
    data() {
      return {
        toggle: {},
        view_more_info: false,
        auto_reload: true,
        file: null,
        msg: 'support_ticket_list',
        items: [],
        items_full: [],
        selected_assigned: '',
        selected_tenant: '',
        selected_category: '',
        selected_priority: '',
        selected_urgency: '',
        search_text: '',
        vip_only: false,
        selected_status: 'open',
        summary: [],
        loading: true,
        totalRows: 1,
        currentPage: 1,
        perPage: 100,
        // default to newest-first by ticket id; b-table syncs these back on
        // header clicks so the user can re-sort without losing the caret.
        sortBy: 'id',
        sortDesc: true,
        // mobile list→detail pattern (matches help articles ≤768px cut-off).
        // isMobileView drives the card view; resize handler keeps it in sync.
        isMobileView: false,
        onWindowResize: null,
        pages: [],
        details_fromDate: justDate(moment().subtract(1, 'year')),
        details_toDate: justDate(moment()),
        assigned_options: [
          {value: '', text: 'All'},
          {value: 'unassigned', text: 'Unassigned'},
          { value: 'ben@initekconsulting.com', text: 'Ben ODay' },
          // jen is a legacy short-username account — see support_ticket_details.vue assigned_options.
          { value: 'jen', text: 'Jen' },
          { value: 'chaz@initekconsulting.com', text: 'Chaz Watson' },
          { value: 'kai@initekconsulting.com', text: 'Kai Klein' },
          { value: 'evan@initekconsulting.com', text: 'Evan Anderson' },
        ],
        dev_team_filters: INITEK_TEAM_FILTER_CHIPS,
        category_options: [
          {value: '', text: 'All'},
          {value: 'general', text: 'General'},
          {value: 'actuals', text: 'Actuals'},
          {value: 'alerts', text: 'Alerts'},
          {value: 'maps', text: 'Map Views'},
          {value: 'reports', text: 'View & Reports'},
          {value: 'vsm', text: 'VSM'},
          {value: 'data', text: 'Data Services'},
          {value: 'fleet-assistant', text: 'Fleet Assistant'},
        ],
        priority_options: [
          {value: '', text: 'All'},
          {value: 'question', text: 'Question/Request'},
          {value: 'minor', text: 'Minor Bug'},
          {value: 'urgent', text: 'Major Bug / System Issue'},
          {value: 'enhancement', text: 'Enhancement Request'},
          {value: 'feature', text: 'New Feature Request'},
        ],
        urgency_options: [
          {value: '', text: 'All'},
          {value: 'low', text: 'Low'},
          {value: 'medium', text: 'Medium'},
          {value: 'high', text: 'High'},
          {value: 'emergency', text: 'Emergency'},
        ],
        inlineAssignedOptions: [
          { value: null, text: 'Unassigned' },
          { value: 'ben@initekconsulting.com', text: 'Ben ODay' },
          { value: 'jen', text: 'Jen' },
          { value: 'chaz@initekconsulting.com', text: 'Chaz Watson' },
          { value: 'kai@initekconsulting.com', text: 'Kai Klein' },
          { value: 'evan@initekconsulting.com', text: 'Evan Anderson' },
        ],
        inlineUrgencyOptions: [
          { value: null, text: '—' },
          { value: 'low', text: 'Low' },
          { value: 'medium', text: 'Medium' },
          { value: 'high', text: 'High' },
          { value: 'emergency', text: 'Emergency' },
        ],
        inlineStatusOptions: [
          { value: 'new', text: 'New' },
          { value: 'queued', text: 'Queued' },
          { value: 'review', text: 'Under Review' },
          { value: 'progress', text: 'In Progress' },
          { value: 'waiting', text: 'Waiting On Customer' },
          { value: 'approved', text: 'Customer Approved' },
          { value: 'testing', text: 'Customer Testing' },
          { value: 'blocked', text: 'Blocked' },
          { value: 'cancelled', text: 'Cancelled' },
          { value: 'completed', text: 'Completed' },
        ],
        inlinePriorityOptions: [
          { value: 'question', text: 'Question/Request' },
          { value: 'minor', text: 'Minor Bug' },
          { value: 'urgent', text: 'Major Bug / System Issue' },
          { value: 'enhancement', text: 'Enhancement Request' },
          { value: 'feature', text: 'New Feature Request' },
        ],
        inlineSaving: {},
        inlineEditing: null,
        inlineDraft: '',
        pokeSaving: {},
        status_options: [
          { value: 'open', text: 'Open'},
          { value: '', text: 'All'},
          { value: 'active', text: 'Active'},
          { value: 'review', text: 'Under Review' },
          { value: 'new', text: 'New' },
          { value: 'queued', text: 'Queued' },
          { value: 'progress', text: 'In Progress' },
          { value: 'waiting', text: 'Waiting On Customer' },
          { value: 'approved', text: 'Customer Approved' },
          { value: 'testing', text: 'Customer Testing' },
          { value: 'blocked', text: 'Blocked' },
          { value: 'cancelled', text: 'Cancelled' },
          { value: 'completed', text: 'Completed' },
        ],
        options: {
          format: 'MM-DD-YYYY',
          useCurrent: true,
          // minDate: moment().subtract(360, 'days'),
          maxDate: moment().add(0, 'days'),
          keepOpen: false,
          showClear: true,
          showClose: true,
          toolbarPlacement: 'top',
        },
        // adminOnly: true marks columns that carry sys-admin-only context
        // (assignee staff name, internal target date). filteredFields strips
        // them when effectiveRole !== 'admin', so both real non-admin users
        // and admins previewing via the Muggle toggle see the same trimmed
        // list. updated_by is left visible for everyone — customers can
        // tell at a glance whether their last reply was acknowledged.
        fields: [
          { key: 'id', label: 'ID', sortable: true },
          { key: 'tenant_id', label: 'Account', sortable: true, thStyle: { width: "200px" } },
          { key: 'priority', label: 'Type', sortable: true },
          { key: 'subject', sortable: true, thStyle: { width: "400px" } },
          { key: 'ticket_status', label: 'Status', sortable: true },
          { key: 'category', label: 'Feature', sortable: true },
          { key: 'urgency', label: 'Urgency', sortable: true, adminOnly: true },
          { key: 'username', label: 'Created By', sortable: true },
          { key: 'created_at', label: 'Created', sortable: true },
          { key: 'updated_by', label: 'Updated By', sortable: true },
          { key: 'updated_at', label: 'Updated', sortable: true },
          { key: 'assigned_to', label: 'Assigned', sortable: true, adminOnly: true },
          { key: 'estimated_finish_date', label: 'Target', sortable: true, adminOnly: true },
          { key: 'actions', label: 'Actions' },
        ],
      }
    },

    async created() {
      this.selected_tenant = this.user.tenant

      this.load_filters()

      await this.load_data()
    },

    mounted() {
      this.isMobileView = window.innerWidth <= 768
      this.onWindowResize = () => {
        this.isMobileView = window.innerWidth <= 768
      }
      window.addEventListener('resize', this.onWindowResize)
    },

    beforeDestroy() {
      if (this.onWindowResize) {
        window.removeEventListener('resize', this.onWindowResize)
      }
    },

    methods: {
      // look up the tenant_options entry and return it if is_vip is true.
      // backend (fleet_service.py login) annotates tenant_options with
      // { is_vip, vip_reason } based on fleet size + multi-tenant group.
      // gated on effectiveRole so non-admin users (and Muggle preview) never
      // see VIP signaling — matches the FI-1647 "always effectiveRole, never
      // user.role" rule. funnels chip / row-class / vip_only filter through
      // one bottleneck.
      getTenantVip(tenantId) {
        if (this.effectiveRole !== 'admin') return null
        if (!tenantId || !this.user || !this.user.tenant_options) return null
        const opt = this.user.tenant_options.find(o => o.value === tenantId)
        return (opt && opt.is_vip) ? opt : null
      },
      // row-level class for b-table. tags VIP rows so the left-edge bar renders;
      // tags completed rows so their text fades gray.
      rowClass(item) {
        if (!item) return ''
        const classes = []
        if (this.getTenantVip(item.tenant_id)) classes.push('stl-row-vip')
        if (item.ticket_status === 'completed') classes.push('stl-row-done')
        return classes.join(' ')
      },
      // compact list timestamp ("4/16 6:39p"); shared with the ops desk
      // list via helpers/dates. full ISO stays on the cell title tooltip.
      formatDateTimeCompact,
      // target date stored as "MM-DD-YYYY"; shown as "4/16" to match the other
      // compact cells. empty → em-dash so the column visibly conveys "no target".
      formatTargetDateCompact(value) {
        if (!value || typeof value !== 'string') return '—'
        const m = moment(value, 'MM-DD-YYYY', true)
        if (!m.isValid()) return '—'
        return m.format('M/D')
      },
      // shortened labels for the status cell so rows stay single-line.
      // the dropdown filter above still uses the full status_options labels.
      statusShortLabel(value) {
        const shortMap = {
          review: 'In Review',
          waiting: 'Waiting',
          approved: 'Approved',
          testing: 'Testing',
          blocked: 'Blocked',
        }
        return shortMap[value] || this.getOptionText(this.status_options, value)
      },
      // map a priority value to its tag styling. returns null for unknown priorities
      // so the template can fall back to plain text.
      // compactLabel is the short all-caps label rendered in the labeled variant
      // (non-admin view); label is the full tooltip text.
      getPriorityTag(priority) {
        const tags = {
          question:    { label: 'Question',           compactLabel: 'QUESTION',  icon: 'fa-question', variant: 'priority-tag-warning' },
          minor:       { label: 'Minor Bug',          compactLabel: 'MINOR BUG', icon: 'fa-bug',      variant: 'priority-tag-orange-light' },
          urgent:      { label: 'Major Bug / Outage', compactLabel: 'MAJOR BUG', icon: 'fa-bolt',     variant: 'priority-tag-danger' },
          enhancement: { label: 'Enhancement',        compactLabel: 'ENHANCE',   icon: 'fa-wrench',   variant: 'priority-tag-info' },
          feature:     { label: 'New Feature',        compactLabel: 'FEATURE',   icon: 'fa-star',     variant: 'priority-tag-success' },
        }
        return tags[priority] || null
      },
      getUrgencyTag(urgency) {
        const tags = {
          low:       { label: 'Low',       compactLabel: 'LOW',       icon: 'fa-minus',                variant: 'priority-tag-info' },
          medium:    { label: 'Medium',    compactLabel: 'MEDIUM',    icon: 'fa-exclamation',          variant: 'priority-tag-warning' },
          high:      { label: 'High',      compactLabel: 'HIGH',      icon: 'fa-exclamation-triangle', variant: 'priority-tag-orange-light' },
          emergency: { label: 'Emergency', compactLabel: 'EMERGENCY', icon: 'fa-bolt',                 variant: 'priority-tag-danger' },
        }
        const level = urgency || 'low'
        const meta = tags[level] || tags.low
        return {
          ...meta,
          label: urgency
            ? (this.getOptionText(this.inlineUrgencyOptions, urgency) || meta.label)
            : 'Unset (Low)',
        }
      },
      show_active(user) {
        this.items = this.items_full
        this.selected_tenant = ''
        this.selected_assigned = this.devTeamAssignedUsername(user)
        this.selected_status = 'active'
        this.sortBy = 'urgency'
        this.sortDesc = true
        this.load_data()
      },
      isDevTeamChipSelected(member) {
        return this.selected_assigned === this.devTeamAssignedUsername(member.user) && this.selected_status === 'active'
      },
      isUnassignedChipSelected() {
        return this.selected_assigned === 'unassigned' && this.selected_status === 'open'
      },
      show_unassigned() {
        this.items = this.items_full
        this.selected_tenant = ''
        this.selected_status = 'open'
        this.selected_assigned = 'unassigned'
        this.load_data()
      },
      show_all_open() {
        this.items = this.items_full
        this.selected_tenant = ''
        this.selected_status = 'open'
        this.selected_assigned = ''
        this.load_data()
      },
      show_all_current_account() {
        this.items = this.items_full
        this.selected_tenant = this.user.tenant
        this.selected_status = 'open'
        this.selected_assigned = ''
        this.load_data()
      },
      save_filters() {
        let filters = {
          "details_fromDate": this.details_fromDate,
          "details_toDate": this.details_toDate,
          "selected_tenant": this.selected_tenant,
          "selected_status": this.selected_status,
          "selected_assigned": this.selected_assigned,
          "selected_category": this.selected_category,
          "selected_priority": this.selected_priority,
          "selected_urgency": this.selected_urgency
        }
        localStorage.setItem('support-filters', JSON.stringify(filters))
      },
      load_filters() {
        let support_filters = localStorage.getItem('support-filters')
        if (support_filters) {
          this.auto_reload = false
          let support_filters_dict = JSON.parse(support_filters)
          if (support_filters_dict.details_fromDate) {
            this.details_fromDate = support_filters_dict.details_fromDate
            // always anchor the right edge to today so returning users don't
            // get a stale upper bound from a previous session (FI-1890)
          }
          this.selected_tenant = support_filters_dict.selected_tenant
          this.selected_status = support_filters_dict.selected_status
          this.selected_assigned = support_filters_dict.selected_assigned
          this.selected_category = support_filters_dict.selected_category
          this.selected_priority = support_filters_dict.selected_priority
          if (support_filters_dict.selected_urgency != null) {
            this.selected_urgency = support_filters_dict.selected_urgency
          }
          this.auto_reload = true
        }
      },
      async load_data() {

        this.loading = true
        try {
          const {value: {data = []}} = await search_support_tickets(this.selected_tenant, this.selected_assigned, this.selected_category, this.selected_priority, this.search_text, this.selected_status,
              moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
              moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
              this.selected_urgency
          )
          this.items_full = data || []
          this.filter_results()
          this.save_filters()

        } catch (e) {
          this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
        }
        this.loading = false
      },
      async delete_support_ticket(tenant_id, id) {
        if (confirm("Are you sure you want to delete this ticket?")) {
          this.$emit('showAlert', "deleting ticket", 'info')
          const result = await delete_support_ticket(tenant_id, id)
          this.$emit('showAlert', "support ticket has been deleted", 'success', 5000)
          await this.load_data()
        }
      },
      pokeSaveKey(ticket) {
        return `${ticket.tenant_id}-${ticket.id}`
      },
      isPokeSaving(ticket) {
        return !!this.pokeSaving[this.pokeSaveKey(ticket)]
      },
      async pokeTicket(ticket) {
        if (!this.user || this.user.role !== 'admin' || this.isPokeSaving(ticket)) return
        const saveKey = this.pokeSaveKey(ticket)
        this.$set(this.pokeSaving, saveKey, true)
        try {
          const { value: { data = {} } } = await poke_support_ticket(ticket.tenant_id, ticket.id)
          const emailed = Number(data.emailed || 0)
          this.$emit('showAlert', data.message || 'Update email sent', emailed ? 'success' : 'warning', 5000)
        } catch (e) {
          this.$emit('showAlert', 'Error sending update email', 'danger', 5000)
        } finally {
          this.$delete(this.pokeSaving, saveKey)
        }
      },
      async shift_detail_graph_date(shift) {
        this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY')
            .add(shift, 'months')
            .format('MM-DD-YYYY')
        this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY')
            .add(shift, 'months')
            .format('MM-DD-YYYY')
      },
      getOptionText(options, value) {
        const option = options.find(opt => opt.value === value)
        return option ? option.text : value
      },
      displayUserName(username, displayNameOverride) {
        if (displayNameOverride) return displayNameOverride
        if (!username) return ''
        const fromInlineAssigned = this.getOptionText(this.inlineAssignedOptions, username)
        if (fromInlineAssigned && fromInlineAssigned !== username && fromInlineAssigned !== '—') {
          return fromInlineAssigned
        }
        const fromAssigned = this.getOptionText(this.assigned_options, username)
        if (fromAssigned && fromAssigned !== username && fromAssigned !== 'All') {
          return fromAssigned
        }
        return String(username).split('@')[0]
      },
      initekTeamImage(username) {
        return initekTeamImageForUsername(username)
      },
      filter_results() {
        this.items = this.items_full
        if (this.search_text)
          this.items = this.items.filter(item => this.contains(item.username,this.search_text) || this.contains(item.subject,this.search_text) || this.contains(item.description,this.search_text) || this.contains(item.tenant_id,this.search_text))
        if (this.vip_only)
          this.items = this.items.filter(item => !!this.getTenantVip(item.tenant_id))
      },
      contains(val1, val2) {
        if (val1 != null) {
          return val1.toLowerCase().indexOf(val2.toLowerCase()) != -1;
        }
        return false;
      },
      devTeamAssignedUsername(user) {
        return user === 'jen' ? 'jen' : user + '@initekconsulting.com'
      },
      sortItems(items, sortBy, sortDesc) {
        if (!items || !items.length || !sortBy) {
          return items ? [...items] : []
        }
        const direction = sortDesc ? -1 : 1
        const compare = (a, b) => {
          let av = a[sortBy]
          let bv = b[sortBy]

          if (sortBy === 'id') {
            return (Number(av) - Number(bv)) * direction
          }
          if (sortBy === 'urgency') {
            av = URGENCY_RANK[av] || URGENCY_RANK.low
            bv = URGENCY_RANK[bv] || URGENCY_RANK.low
            const urgencyDiff = (av - bv) * direction
            if (urgencyDiff !== 0) return urgencyDiff
            const updatedDiff = (b.updated_at || '').localeCompare(a.updated_at || '')
            if (updatedDiff !== 0) return updatedDiff
            return Number(b.id) - Number(a.id)
          }
          if (sortBy === 'priority') {
            av = PRIORITY_RANK[av] || 0
            bv = PRIORITY_RANK[bv] || 0
            return (av - bv) * direction
          }

          const aNull = av == null || av === ''
          const bNull = bv == null || bv === ''
          if (aNull && bNull) return Number(b.id) - Number(a.id)
          if (aNull) return 1
          if (bNull) return -1

          if (typeof av === 'number' && typeof bv === 'number') {
            const diff = (av - bv) * direction
            return diff !== 0 ? diff : Number(b.id) - Number(a.id)
          }

          const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' })
          return cmp !== 0 ? cmp * direction : Number(b.id) - Number(a.id)
        }
        return [...items].sort(compare)
      },
      inlineSaveKey(ticket, field = '') {
        return `${ticket.tenant_id}-${ticket.id}${field ? '-' + field : ''}`
      },
      inlineEditKey(ticket, field) {
        return `${ticket.tenant_id}-${ticket.id}:${field}`
      },
      isInlineEditing(ticket, field) {
        return this.inlineEditing === this.inlineEditKey(ticket, field)
      },
      startInlineEdit(ticket, field, event) {
        if (this.effectiveRole !== 'admin' || this.isInlineSaving(ticket, field)) return
        if (event && event.stopPropagation) event.stopPropagation()
        this.inlineEditing = this.inlineEditKey(ticket, field)
        if (field === 'subject') {
          this.inlineDraft = ticket.subject || ''
        }
      },
      clearInlineEdit() {
        this.inlineEditing = null
        this.inlineDraft = ''
      },
      commitInlineSubject(ticket) {
        if (!this.isInlineEditing(ticket, 'subject')) return
        const trimmed = (this.inlineDraft || '').trim()
        const current = (ticket.subject || '').trim()
        if (!trimmed || trimmed === current) {
          this.clearInlineEdit()
          return
        }
        this.updateTicketInline(ticket, 'subject', trimmed)
      },
      onInlineSubjectBlur(ticket) {
        setTimeout(() => {
          if (!this.isInlineEditing(ticket, 'subject') || this.isInlineSaving(ticket, 'subject')) return
          this.commitInlineSubject(ticket)
        }, 120)
      },
      onInlineEditBlur(ticket, field) {
        setTimeout(() => {
          if (this.isInlineEditing(ticket, field) && !this.isInlineSaving(ticket, field)) {
            this.clearInlineEdit()
          }
        }, 120)
      },
      isInlineSaving(ticket, field = '') {
        if (field) {
          return !!this.inlineSaving[this.inlineSaveKey(ticket, field)]
        }
        return Object.keys(this.inlineSaving).some(key => key.startsWith(this.inlineSaveKey(ticket)))
      },
      patchTicketInLists(tenantId, id, updates) {
        const apply = (list) => {
          const idx = list.findIndex(t => t.tenant_id === tenantId && Number(t.id) === Number(id))
          if (idx >= 0) {
            Object.assign(list[idx], updates)
          }
        }
        apply(this.items)
        apply(this.items_full)
      },
      async updateTicketInline(ticket, field, value) {
        if (this.effectiveRole !== 'admin') return
        let normalized = value === '' ? null : value
        if (field === 'subject') {
          normalized = (value || '').trim()
          if (!normalized) {
            this.clearInlineEdit()
            return
          }
        }
        if ((ticket[field] || null) === (normalized || null)) {
          this.clearInlineEdit()
          return
        }

        const saveKey = this.inlineSaveKey(ticket, field)
        if (this.inlineSaving[saveKey]) return
        this.$set(this.inlineSaving, saveKey, true)

        try {
          const { value: { data = {} } } = await get_support_ticket(ticket.tenant_id, ticket.id)
          if (!data.item) {
            throw new Error('ticket not found')
          }
          const model = { ...data.item, new_tenant_id: data.item.tenant_id }
          model[field] = normalized
          await update_support_ticket(ticket.tenant_id, model, false)
          this.patchTicketInLists(ticket.tenant_id, ticket.id, { [field]: normalized })
        } catch (e) {
          this.$emit('showAlert', 'Error updating ticket, try again or open the ticket to edit', 'danger', 5000)
        } finally {
          this.$delete(this.inlineSaving, saveKey)
          this.clearInlineEdit()
        }
      },
    },

    computed: {
      ...mapState({
        user: state => state.profile.user
      }),
      filteredFields() {
        const isAdminView = this.effectiveRole === 'admin'
        return this.fields.filter(field => {
          // sys-admin-only columns disappear in non-admin views (real
          // customers and the Muggle preview alike).
          if (field.adminOnly && !isAdminView) {
            return false;
          }
          if ((!isAdminView && this.user.tenant_options.length === 1) && field.key === 'tenant_id') {
            return false;
          }
          return true;
        })
      },
      // admins can opt into a "view as non-admin" preview via the navbar toggle;
      // everywhere we gate UI on admin-ness, use this instead of user.role so
      // the preview toggle actually affects the render.
      effectiveRole() {
        const viewAs = this.$store && this.$store.state && this.$store.state.profile && this.$store.state.profile.view_as
        if (this.user && this.user.role === 'admin' && viewAs === 'non-admin') return 'customer'
        return this.user && this.user.role
      },
      search_tenant_options() {
        let options = [{value: '', text: 'All'}]
        return options.concat(this.user.tenant_options)
      },
      // mirror of b-table's current-page slicing so the mobile card list
      // paginates the same way as the desktop table; both views bind to
      // currentPage + perPage so the pagination control affects both.
      paginatedItems() {
        const start = (this.currentPage - 1) * this.perPage
        return this.sortedItems.slice(start, start + this.perPage)
      },
      sortedItems() {
        return this.sortItems(this.items, this.sortBy, this.sortDesc)
      },
    },

    watch: {
      support_ticket: {
        deep: true,
        handler() {
          this.load_data()
        }
      },
      vip_only() {
        this.filter_results()
      },
      search_text() {
        this.filter_results()
      },
      selected_tenant(newDate) {
        if (this.auto_reload)
          this.load_data()
      },
      selected_category(newDate) {
        if (this.auto_reload)
          this.load_data()
      },
      selected_status(newDate) {
        if (this.auto_reload)
          this.load_data()
      },
      selected_assigned(newDate) {
        if (this.auto_reload)
          this.load_data()
      },
      selected_priority(newDate) {
        if (this.auto_reload)
          this.load_data()
      },
      selected_urgency(newDate) {
        if (this.auto_reload)
          this.load_data()
      },
    }
  }
</script>

<style scoped>
/* horizontal padding kept minimal so the table + pagination span the full page
   width — prior 20px side padding + a nested 20px-margin wrapper were eating
   ~80px per side. vertical padding stays for visual breathing room under navbar */
.stl-root {
  padding: 10px 0;
}

/* wrapper around the b-table + pagination. vertical margin only so the table
   tracks the page edges. replaces the prior inline `margin:20px`. */
.stl-table-wrap {
  margin: 10px 0 20px;
}

/* mobile card list — only rendered when isMobileView=true (≤768px).
   each card is a router-link that navigates to the ticket detail page,
   matching the list→detail pattern used by the help articles tab
   (same 768px cut-off). card shows: id + priority tag, subject,
   status/tenant/updated meta row, and (admin only) the assignee. */
.stl-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 6px 10px 16px;
}
.stl-mobile-card {
  display: block;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 10px 12px;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.12s ease;
}
.stl-mobile-card:hover,
.stl-mobile-card:focus {
  background: var(--fi-brand-tint);
  text-decoration: none;
  color: inherit;
}
.stl-mobile-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}
.stl-mobile-card-id {
  font-size: 12px;
  color: #888;
  font-weight: 600;
}
.stl-mobile-card-subject {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
  word-break: break-word;
}
.stl-mobile-card-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #555;
}
.stl-mobile-card-sep { color: #ccc; }
.stl-mobile-card-admin {
  font-size: 11px;
  color: #777;
  margin-top: 4px;
}
.stl-mobile-card--muted { opacity: 0.55; }

/* skeleton variant of the mobile card. inert (no hover/cursor) so the
   user can't tap into a half-loaded ticket. shimmer animation runs on
   the bars themselves; card frame stays static. */
.stl-mobile-card--skeleton {
  pointer-events: none;
  cursor: default;
  background: #fff;
}
.stl-mobile-card--skeleton:hover { background: #fff; }
.stl-skeleton {
  display: inline-block;
  background: linear-gradient(90deg, #ececec 0%, #f5f5f5 50%, #ececec 100%);
  background-size: 200% 100%;
  border-radius: 4px;
  animation: stl-skeleton-shimmer 1.4s ease-in-out infinite;
}
@keyframes stl-skeleton-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.stl-skeleton--id      { width: 38px; height: 12px; }
.stl-skeleton--tag     { width: 86px; height: 20px; border-radius: 4px; }
.stl-skeleton--subject { display: block; width: 90%; height: 15px; margin: 8px 0 0; }
.stl-skeleton--subject-2 { width: 60%; margin-top: 4px; }
.stl-skeleton-meta-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}
.stl-skeleton--meta-status { width: 60px; height: 11px; }
.stl-skeleton--meta-tenant { width: 100px; height: 11px; }
.stl-skeleton--meta-time   { width: 50px; height: 11px; }
.stl-skeleton--admin {
  display: block;
  width: 80px;
  height: 11px;
  margin-top: 8px;
}
.stl-mobile-card--vip { border-left: 3px solid #b58100; }
.stl-mobile-empty {
  color: #888;
  font-size: 13px;
  text-align: center;
  padding: 20px 10px;
}
.stl-mobile-card-tenant.is-vip {
  color: #7a5400;
  font-weight: 600;
}

/* inner wrapper gives the table a small breath of space from the page edges
   without affecting the pagination row below, which stays flush-wide and
   centers its own controls via b-pagination `align="center"`. */
.stl-table-inner {
  margin: 0 10px;
}

.date-nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid #ccc;
  background: #f0f0f0;
  color: #333;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.date-nav-btn:hover {
  background: #ddd;
  border-color: #aaa;
}

@media (max-width: 768px) {
  .stl-status-shortcuts,
  .stl-name-shortcuts {
    display: none;
  }
}

</style>

<style>
/* sort indicators inline instead of floating above — use simple descendant
   selectors so they work regardless of BV's sticky-header wrapper structure */
.stl-root th[aria-sort] {
  background-image: none !important;
  padding-right: 0.75rem !important;
  white-space: nowrap;
}

/* hide BV's default ::before sort icons */
.stl-root th[aria-sort]::before {
  display: none !important;
}

.stl-root th[aria-sort]::after {
  display: inline-block !important;
  margin-left: 4px;
  font-size: 10px;
  color: #999;
  vertical-align: middle;
  float: none !important;
  width: auto !important;
  height: auto !important;
  opacity: 1 !important;
}

.stl-root th[aria-sort="none"]::after {
  content: "\2195" !important;
}

.stl-root th[aria-sort="ascending"]::after {
  content: "\25B2" !important;
  color: var(--fi-rail-accent);
}

.stl-root th[aria-sort="descending"]::after {
  content: "\25BC" !important;
  color: var(--fi-rail-accent);
}

/* compact table: remove vertical borders, keep horizontal rules */
.stl-root .table.b-table {
  font-size: 13px;
  border-collapse: collapse;
}

.stl-root .table.b-table th,
.stl-root .table.b-table td {
  border-left: none !important;
  border-right: none !important;
  border-bottom: 1px solid #e0e0e0;
  /* !important needed because 15+ unscoped component stylesheets globally
     set '.table td { padding: 0.1rem !important }' (a long-standing leak from
     non-scoped Vue <style> blocks in other components). */
  padding: 3px 8px !important;
  vertical-align: middle !important;
  text-align: left !important;
}
/* first/last cells hug the page edges a little more so the table visually
   reads as full-width without the content feeling cramped against the edge */
.stl-root .table.b-table th:first-child,
.stl-root .table.b-table td:first-child {
  padding-left: 12px !important;
}
.stl-root .table.b-table th:last-child,
.stl-root .table.b-table td:last-child {
  padding-right: 12px !important;
}

.stl-root .table.b-table thead th {
  background: #f8f9fa;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #555;
  /* sticky column headers — stay pinned to viewport top while the page scrolls,
     without introducing a nested scroll container on the table itself */
  position: sticky;
  top: 0;
  z-index: 2;
  /* box-shadow draws the divider line instead of border-bottom.
     needed because:
       1. border-bottom disappears on sticky <th> when border-collapse: collapse
       2. ::after is already used by the sort-arrow rules
     shadow stays fixed to the th through sticky positioning. */
  box-shadow: inset 0 -2px 0 0 #ccc;
}

.stl-root .table.b-table tbody tr:hover td {
  background-color: var(--fi-brand-tint);
}

/* priority tags — muted pastel palette matching the callout design language.
   see feedback_tone_and_callouts.md: gentle, informational, not alarming.
   to be rolled into the shared Callout Panel primitive during NC Phase 2. */
.stl-root .priority-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  /* line-height: 1 everywhere inside the pill so the icon box and the
     label box have the same height as their glyphs — otherwise an
     inherited container line-height on the <i> would push it off the
     label's optical baseline (flex `align-items: center` aligns the
     box centers, not the glyph centers). */
  line-height: 1;
  border-radius: 4px;
  border: 1px solid;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
/* icon-only variant used in the admin list view — label moves to title tooltip
   so the cell stays narrow. the full-text variant is still used in ticket
   details and the non-admin list view (priority-tag--labeled).
   fixed icon width keeps every box the same compact size regardless of which
   FA glyph is inside (otherwise narrow glyphs like `?` render a tighter box
   than wider ones like bug/wrench/star). */
.stl-root .priority-tag--icon-only {
  padding: 3px 4px;
  gap: 0;
  cursor: help;
  line-height: normal;
  align-items: center;
  justify-content: center;
}
.stl-root .priority-tag .fa {
  font-size: 11px;
  line-height: 1;
}
.stl-root .priority-tag--icon-only .fa {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  width: 12px;
  height: 13px;
  line-height: 1;
  text-align: center;
  overflow: visible;
}
/* labeled variant: icon + all-caps compact label. more prominent than the
   icon-only column so non-admins can scan priority without needing to learn
   the icon glyphs. */
.stl-root .priority-tag--labeled {
  /* match the icon-only variant's 5px horizontal gutter so labeled
     and icon-only pills read as the same family — the earlier 8px
     padding left noticeable empty space on either side of the label. */
  padding: 3px 5px;
  gap: 5px;
  cursor: default;
}
.stl-root .priority-tag--labeled .fa {
  font-size: 12px;
  width: 14px;
  text-align: center;
}
.stl-root .priority-tag-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  line-height: 1;
}
/* icon header for the priority column: sits in place of the "Priority" label so
   the column stays narrow. sort caret is still rendered by b-table next to it. */
.stl-root .stl-priority-head {
  font-size: 14px;
  color: #4a5568;
  cursor: help;
}
/* center the icon + sort caret in the priority column header; overrides the
   default left alignment applied to all th/td. */
.stl-root .table.b-table th.stl-priority-th {
  text-align: center !important;
}
.stl-root .priority-tag-info {
  background: #eaf3fb;
  border-color: #4a7ba7;
  color: #1f4a73;
}
.stl-root .priority-tag-warning {
  background: #fff4d1;
  border-color: #b58100;
  color: #7a5400;
}
/* Major Bug / Outage: outlined in the same pastel+border family as the other
   priorities so the column reads as one coherent set; severity still pops via
   the flash animation on the bolt glyph (see .priority-tag--strike below). */
.stl-root .priority-tag-danger {
  background: #fbe3e5;
  border-color: #dc3545;
  color: #dc3545;
}
/* strobe-style flash animation runs only on open urgent rows; the bolt
   briefly spikes brighter and scales up to read as a "lightning strike"
   rather than a constant throb. */
.stl-root .priority-tag--strike .fa-bolt {
  animation: priority-bolt-strike 2s infinite ease-out;
  transform-origin: center center;
}
@keyframes priority-bolt-strike {
  0%, 14%, 100% { transform: scale(1);   filter: brightness(1); }
  4%            { transform: scale(1.25); filter: brightness(1.7); }
  8%            { transform: scale(1);   filter: brightness(1); }
  10%           { transform: scale(1.2); filter: brightness(1.6); }
  13%           { transform: scale(1);   filter: brightness(1); }
}
.stl-root .priority-tag-success {
  background: #edf7ee;
  border-color: #3a8144;
  color: #1f5727;
}
/* outlined orange for Minor Bug — soft orange tint matching the pastel fills
   used by the other outlined priorities (question/enhancement/feature). */
.stl-root .priority-tag-orange-light {
  background: #ffe8d4;
  border-color: #ff6d00;
  color: #ff6d00;
}
/* muted variant — applied when the ticket is completed so the user's eye
   can focus on open/active tickets. opacity also fades the border and icon. */
.stl-root .priority-tag-muted {
  opacity: 0.45;
}

/* VIP signal: tenant name renders in a darker gold with heavier weight.
   chosen over an icon to keep the row visually quiet and non-competing
   with the priority tag in the next column. tooltip carries the reason.
   single-line + ellipsis keeps row heights uniform so column contents
   stay visually aligned across rows. */
.stl-root .tenant-name {
  display: inline-block;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
.stl-root .stl-inline-select {
  min-width: 110px;
  max-width: 140px;
  font-size: 13px;
  padding: 0 1.5rem 0 0.35rem;
  height: calc(1.5em + 0.5rem + 2px);
}
.stl-root .stl-inline-select--status {
  min-width: 120px;
  max-width: 160px;
}
.stl-root .stl-inline-select--type {
  min-width: 140px;
  max-width: 180px;
}
.stl-root .stl-inline-input {
  font-size: 13px;
  min-width: 160px;
}
.stl-root .stl-inline-input.stl-subject {
  width: 100%;
  max-width: 400px;
}
.stl-root .stl-inline-readonly {
  cursor: pointer;
  border-bottom: 1px dashed transparent;
}
.stl-root .stl-inline-readonly:hover,
.stl-root .stl-inline-readonly:focus {
  border-bottom-color: #999;
  outline: none;
}
.stl-root .stl-person-display {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  vertical-align: middle;
}
.stl-root .stl-team-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #b8d0e4;
  box-shadow: 0 1px 3px rgba(70, 130, 180, 0.12);
  flex: 0 0 auto;
}
.stl-root .priority-tag.stl-inline-readonly {
  border-bottom: none;
  cursor: pointer;
}
.stl-root .priority-tag.stl-inline-readonly:hover,
.stl-root .priority-tag.stl-inline-readonly:focus {
  border-bottom: none;
  filter: brightness(0.92);
}
.stl-root .stl-poke-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  padding: 0;
  border: none;
  background: transparent;
  color: #555;
  cursor: pointer;
}
.stl-root .stl-poke-btn:disabled {
  cursor: default;
  opacity: 0.65;
}
.stl-root .stl-poke-btn:not(:disabled):hover {
  color: #007bff;
}
/* admin filter row 2: flex + gap gives consistent spacing; dedicated separator
   spans render as subtle vertical rules that collapse gracefully on wrap. */
.stl-root .stl-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px 10px;
  margin: 6px 0;
  font-size: 14px;
  font-weight: bold;
}
.stl-root .stl-fbar-count {
  white-space: nowrap;
}
.stl-root .stl-fbar-field {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}
.stl-root .stl-fbar-select {
  font-size: 14px;
  font-weight: bold;
}
.stl-root .stl-fbar-input {
  font-size: 14px;
  width: 130px;
}
.stl-root .stl-btn-group {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}
.stl-root .stl-dev-team-chips {
  gap: 6px;
}
.stl-root .stl-dev-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
}
.stl-root .stl-dev-chip:hover {
  border-color: #6c757d;
  transform: translateY(-1px);
}
.stl-root .stl-dev-chip--selected {
  border-color: var(--fi-action-primary);
  box-shadow: 0 0 0 1px rgba(0, 123, 255, 0.35);
}
.stl-root .stl-dev-chip-img {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}
.stl-root .stl-dev-chip-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #eef0f2;
  color: #666;
  font-size: 14px;
  line-height: 1;
}
.stl-root .stl-dev-chip-icon--unassigned::after {
  content: '';
  position: absolute;
  left: 3px;
  right: 3px;
  top: 50%;
  height: 2px;
  margin-top: -1px;
  background: #c0392b;
  transform: rotate(-45deg);
  border-radius: 1px;
  pointer-events: none;
}
.stl-root .stl-fbar-sep {
  display: inline-block;
  width: 1px;
  height: 18px;
  background: #ccc;
}
.stl-root .stl-fbar-vip {
  font-size: 14px;
  font-weight: bold;
  margin: 0;
  white-space: nowrap;
}

/* below the navbar's mobile breakpoint, the quick-filter button groups and
   their separators wrap into a tall stack that dwarfs the real filters. hide
   them so the remaining dropdowns + search + VIP toggle stay usable. */
@media (max-width: 767px) {
  .stl-root .stl-btn-group,
  .stl-root .stl-fbar-sep {
    display: none;
  }
}

/* VIP signal lives on the row background now; tenant name stays uniform with
   non-VIP rows. cursor stays as a hover affordance for the tooltip. */
.stl-root .tenant-name.is-vip {
  cursor: help;
}
/* fade when the ticket is completed/cancelled so the eye stays on open work */
.stl-root .tenant-name.is-muted {
  opacity: 0.55;
}

/* VIP signal: thin gold bar on the row's leftmost edge. a pseudo-element lets
   us switch between solid (open) and hollow (closed) fills without shifting
   cell content or disrupting BV's default table borders. */
.stl-root .stl-row-vip td:first-child {
  position: relative;
}
.stl-root .stl-row-vip td:first-child::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: #d4a017;
  box-sizing: border-box;
}
/* closed VIP tickets get a hollow version: gold outline, white interior. */
.stl-root .stl-row-vip.stl-row-done td:first-child::before {
  background: #fff;
  border: 1px solid #d4a017;
}
/* completed tickets fade to gray across the whole row so active work stands
   out. !important on the link color beats an app-level anchor rule inherited
   from the help-center wrapper. action icons are png so we fade via opacity
   rather than color. */
.stl-root .stl-row-done td,
.stl-root .stl-row-done td a,
.stl-root .stl-row-done td a:hover {
  color: #9098a1 !important;
}
.stl-root .stl-row-done td img {
  opacity: 0.4;
}

/* subject wraps to show full text; column width caps line length. */
.stl-root .stl-subject {
  display: inline-block;
  max-width: 400px;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  vertical-align: middle;
  line-height: 1.35;
}

/* status cell — single-line, prevents the two-line wrap that was happening for
   "Under Review" / "Customer Testing" and was inflating row height. full label
   stays in the title tooltip (see statusShortLabel map). */
.stl-root .stl-status {
  display: inline-block;
  white-space: nowrap;
  cursor: help;
}

/* compact datetime cell — single-line; full ISO available on hover via :title */
.stl-root .stl-dt {
  display: inline-block;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.stl-root .stl-dt-overdue {
  color: darkred;
}

.error td {
  background-color: rgba(255, 0, 0, 0.25)
}

.success td {
  background-color: hsla(120, 100%, 75%, 0.3)
}

.unprocessed td {
  background-color: hsla(200, 40%, 80%, 0.3)
}

.cancelled td {
  background-color: hsla(290, 60%, 70%, 0.3)
}

.header {
  font-weight: bold;
}

.list-text {
  text-align: left;
  margin-top: 7px;
  margin-left: 7px;
}

.list-option {
  text-align: left;
  margin: 2px;
}
</style>
